import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Directory.css';
import { profileService } from '../services/api';

const Directory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    expertise: '',
    institution: '',
    language: ''
  });
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expertiseOptions, setExpertiseOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  
  // Charger les profils au chargement de la page
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        console.log('Récupération des profils approuvés...');
        const data = await profileService.getProfiles();
        console.log(`${data.length} profils approuvés récupérés`);
        setProfiles(data);
        
        // Extraire les options d'expertise uniques
        const allExpertise = data.flatMap(profile => profile.expertise || []);
        const uniqueExpertise = [...new Set(allExpertise)];
        console.log(`${uniqueExpertise.length} expertises uniques extraites des profils approuvés`);
        setExpertiseOptions(uniqueExpertise);
        
        // Extraire les options d'institutions uniques
        const allInstitutions = data.flatMap(profile => 
          profile.titles ? profile.titles.map(title => {
            // Gérer le cas où organization est un objet ou une chaîne
            if (typeof title.organization === 'object') {
              return title.organization?.name || '';
            }
            return title.organization || '';
          }).filter(org => org) : []
        );
        const uniqueInstitutions = [...new Set(allInstitutions)];
        console.log(`${uniqueInstitutions.length} institutions uniques extraites des profils approuvés`);
        setInstitutionOptions(uniqueInstitutions);
        
        // Extraire les options de langues uniques
        const allLanguages = data.flatMap(profile => profile.languages || []);
        const uniqueLanguages = [...new Set(allLanguages)];
        console.log(`${uniqueLanguages.length} langues uniques extraites des profils approuvés`);
        setLanguageOptions(uniqueLanguages);
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des profils:', err);
        setError('Impossible de charger les profils. Veuillez réessayer plus tard.');
        setLoading(false);
      }
    };
    
    fetchProfiles();
  }, []);
  
  // Rechercher des profils avec l'API
  useEffect(() => {
    const searchProfiles = async () => {
      if (!searchTerm && !filters.expertise && !filters.institution && !filters.language) {
        return; // Ne pas faire de recherche si aucun critère n'est spécifié
      }
      
      try {
        setLoading(true);
        const params = {};
        
        if (searchTerm) params.query = searchTerm;
        if (filters.expertise) params.expertise = filters.expertise;
        if (filters.institution) params.organization = filters.institution;
        if (filters.language) params.language = filters.language;
        
        // Le statut 'approved' est géré par le backend, mais nous le mentionnons ici pour la clarté
        console.log('Recherche de profils approuvés avec les paramètres:', params);
        
        const data = await profileService.searchProfiles(params);
        console.log(`${data.length} profils approuvés trouvés correspondant aux critères`);
        
        setProfiles(data);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la recherche de profils:', err);
        setError('Impossible de rechercher des profils. Veuillez réessayer plus tard.');
        setLoading(false);
      }
    };
    
    // Délai de 500ms pour éviter trop de requêtes pendant la saisie
    const delaySearch = setTimeout(() => {
      searchProfiles();
    }, 500);
    
    return () => clearTimeout(delaySearch);
  }, [searchTerm, filters.expertise, filters.institution, filters.language]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };
  
  // Filtrer les profils en fonction des critères de recherche (filtrage côté client)
  const filteredProfiles = profiles.filter(profile => {
    // Si la recherche est vide, on affiche tous les profils
    if (!searchTerm && !filters.expertise && !filters.institution && !filters.language) {
      return true;
    }
    
    // Sinon, on filtre selon les critères
    const matchesSearch = !searchTerm || 
      profile.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.expertise?.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesExpertise = !filters.expertise || 
      profile.expertise?.includes(filters.expertise);
    
    const matchesInstitution = !filters.institution || 
      profile.titles?.some(title => {
        const org = typeof title.organization === 'object' 
          ? title.organization?.name 
          : title.organization;
        return org === filters.institution;
      });
      
    const matchesLanguage = !filters.language || 
      profile.languages?.includes(filters.language);
    
    return matchesSearch && matchesExpertise && matchesInstitution && matchesLanguage;
  });
  
  return (
    
    <div className="directory-page">
      <div className="container">
        <h1 className="page-title">Répertoire d’expertises</h1>
        
        <div className="search-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou expertise..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <button className="search-button">
              <i className="fas fa-search"></i>
            </button>
          </div>
          
          <div className="filters">
            <div className="filter-group">
              <label htmlFor="expertise">Expertise</label>
              <select
                id="expertise"
                name="expertise"
                value={filters.expertise}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">Toutes les expertises</option>
                {expertiseOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="institution">Institution</label>
              <select
                id="institution"
                name="institution"
                value={filters.institution}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">Toutes les institutions</option>
                {institutionOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="language">Langue</label>
              <select
                id="language"
                name="language"
                value={filters.language}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">Toutes les langues</option>
                {languageOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Chargement des profils...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="btn btn-primary">
              Réessayer
            </button>
          </div>
        ) : (
          <>
            <div className="results-info">
              <p>{filteredProfiles.length} résultat(s) trouvé(s)</p>
            </div>
            
            <div className="experts-grid">
              {filteredProfiles.length > 0 ? filteredProfiles.map(profile => (
                <div key={profile._id} className="expert-card">
                  <div className="expert-photo">
                    <img 
                      src={profile.photo && profile.photo.trim() ? profile.photo : '/images/placeholder-user.jpg'} 
                      alt={`${profile.firstName} ${profile.lastName}`} 
                    />
                  </div>
                  <div className="expert-info">
                    <h3 className="expert-name">{profile.firstName} {profile.lastName}</h3>
                    {profile.titles && profile.titles.length > 0 && (
                      <p className="expert-title">
                        {profile.titles[0].title} - {typeof profile.titles[0].organization === 'object' 
                          ? profile.titles[0].organization?.name || 'N/A'
                          : profile.titles[0].organization || 'N/A'}
                      </p>
                    )}
                    {profile.titles && profile.titles.length > 1 && (
                      <p className="expert-title">
                        {profile.titles[1].title} - {typeof profile.titles[1].organization === 'object'
                          ? profile.titles[1].organization?.name || 'N/A'
                          : profile.titles[1].organization || 'N/A'}
                      </p>
                    )}
                    <div className="expert-expertise">
                      {profile.expertise && profile.expertise.map((exp, index) => (
                        <span key={index} className="badge badge-primary">{exp}</span>
                      ))}
                    </div>
                    <div className="expert-languages">
                      {profile.languages && profile.languages.map((lang, index) => (
                        <span key={index} className="badge badge-secondary">{lang}</span>
                      ))}
                    </div>
                    <Link to={`/profile/${profile._id}`} className="btn btn-outline btn-sm">Voir le profil</Link>
                  </div>
                </div>
              )) : (
                <div className="no-results">
                  <p>Aucun profil ne correspond à votre recherche.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Directory;
